import { ChangeEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, ForcedCase, Label, Layout, LookupModelSelectionEvent, Panel, SaveButton, SlideoutDecorator, SlideoutDecoratorProps, Textbox, Toast } from "@mcleod/components";
import { AutogenLayoutSeating } from "./autogen/AutogenLayoutSeating";
import { EquipmentGroupValidation, EquipmentGroupValidationResult } from "./EquipmentGroupValidation";
import { CommonDialogs } from "@mcleod/common";
import { DateUtil, HorizontalAlignment, ModelRow, ModelSearchResult, ObjectUtil } from "@mcleod/core";
import { getDispatchControlBoolean, LMEGeneralSettings } from "@mcleod/general";
import { ModelTractor } from "../../asset/src/models/ModelTractor";
import { TrailerUtil } from "../TrailerUtil";

export enum SeatingEquipmentType {
    TRACTOR = "Tractor",
    TRAILER = "Trailer"
}

interface SeatingSlideoutProps {
    equipmentId: string;
    equipmentType: SeatingEquipmentType;
    driverLmData?: ModelRow;
    driver2LmData?: ModelRow;
    forceMasterfileUpdate?: boolean;
    doAfterSave?: () => void;
}

export enum Action {
    SEAT = "S",
    UNSEAT = "U",
    CHANGE_DRIVER = "C",
    MOVE = "M"
}

export class Seating extends AutogenLayoutSeating {

    private equipmentId: string;
    private equipmentType: SeatingEquipmentType;
    private driverId: string;
    private driver2Id: string;
    private lockD1: boolean = false;
    private lockD2: boolean = false;
    private forceMasterfileUpdate: boolean;
    private doAfterSave: () => void;
    private maxTrailers = LMEGeneralSettings.getSingleton().getDispatchControl().maximum_trailers;
    private saveButton: SaveButton = undefined;

    public static showSlideout(props: SeatingSlideoutProps, slideoutProps?: Partial<SlideoutDecoratorProps>) {
        const { equipmentId, equipmentType, forceMasterfileUpdate, doAfterSave } = props;
        const layout = Layout.getLayout("lme/dispatch/asset/Seating") as Seating;
        const saveButton = SaveButton.create({
            rowBreak: false,
            marginRight: 8,
            caption: "Save & Close"
        });
        layout.saveButton = saveButton;
        let width = "50%";
        if (equipmentType == SeatingEquipmentType.TRACTOR && layout.maxTrailers && layout.maxTrailers > 1) {
            width = layout.maxTrailers === 2 ? "60%" : "65%";
        }
        layout.equipmentId = equipmentId;
        layout.equipmentType = equipmentType;
        layout.forceMasterfileUpdate = forceMasterfileUpdate;
        layout.doAfterSave = doAfterSave;
        layout.addLayoutLoadListener(() => {
            if (equipmentType == SeatingEquipmentType.TRACTOR ) {
                layout.panelTrailers.rowBreak = true;
            }
            saveButton.dataSource = layout.mainDataSource;
            // Added on layout load to await dataSource initialization
            new SlideoutDecorator({
                layout: layout,
                fillVerticalSpace: true,
                width: width,
                title: `Locate ${equipmentType}`,
                overlayProps: {greyedBackground: true},
                addlComponents: [saveButton],
                ...slideoutProps
            });
        });
    }

    onLoad(): void | Promise<void> {
        this.addBeforeLookupModelListeners();
        this.setupLocationComponents();
        this.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
            if (!this.mainDataSource.validate()) {
                return false;
            }
            const continuity: { all: boolean; d1: any; d2: any; tractor: any; t1: any; t2: any; t3: any } = await this.checkContinuity(this.activeRow);
            if (continuity.all === false) {
                if (continuity.tractor) {
                    CommonDialogs.showError(
                        `Tractor ${this.activeRow.get("tractor_id")} cannot be located before ${DateUtil.formatDateTime(continuity.tractor)}.`, "Continuity Warning"
                    );
                    return false;
                }
                if (continuity.d1 || continuity.d2) {
                    if (!getDispatchControlBoolean("driver_assignments", false)) {
                        const locateTractorWithoutDrivers = await CommonDialogs.showYesNo(
                            this.createDriverContinuityPanel(this.activeRow, continuity),
                            "Continuity Warning"
                        );
                        if (locateTractorWithoutDrivers) {
                            // Clear both drivers if user confirms
                            this.activeRow.setValueAndLookupModelData("driver_id", null, null, null);
                            this.activeRow.set("driver_id", null);
                            this.activeRow.setValueAndLookupModelData("driver_two_id", null, null, null);
                            this.activeRow.set("assigned_driver_changed", false);
                            this.activeRow.set("team_driver_changed", false);
                        } else {
                            return false;
                        }
                    } else {
                        CommonDialogs.showError("Cannot proceed due to continuity issues with the selected driver(s).");
                        return false;
                    }
                }

                // --- Check trailer continuity after driver continuity logic ---
                if (this.equipmentType === SeatingEquipmentType.TRACTOR && (continuity.t1 || continuity.t2 || continuity.t3)) {
                    const locateTractorWithoutTrailers = await CommonDialogs.showYesNo(
                        this.createTrailerContinuityPanel(this.activeRow, continuity),
                        "Trailer Continuity Warning"
                    );
                    if (locateTractorWithoutTrailers) {
                        // Only clear trailers that have continuity errors
                        if (continuity.t1) {
                            this.activeRow.setValueAndLookupModelData("trailer_id", null, null, null);
                            this.activeRow.set("trailer_id", null);
                        }
                        if (continuity.t2) {
                            this.activeRow.setValueAndLookupModelData("trailer2_id", null, null, null);
                            this.activeRow.set("trailer2_id", null);
                        }
                        if (continuity.t3) {
                            this.activeRow.setValueAndLookupModelData("trailer3_id", null, null, null);
                            this.activeRow.set("trailer3_id", null);
                        }
                        return true;
                    }
                    return false;
                }
                return true;
            }

            return true;
        };
         if (this.equipmentType === SeatingEquipmentType.TRACTOR) {
            TrailerUtil.setupMaxTrailers(this.textboxTrailer, this.textboxTrailer2, this.textboxTrailer3);
        }
    }

    addBeforeLookupModelListeners() {
        this.locationSeatedLocation.textCombined.addBeforeLookupModelSearchListener(event => {
            if (this.checkboxTerminalLocations.checked) {
                event.filter.is_terminal = 'Y';
            }
        });
        this.textboxTrailer.addBeforeLookupModelSearchListener(event => event.filter.service_status = 'A');
        this.textboxTrailer2.addBeforeLookupModelSearchListener(event => event.filter.service_status = 'A');
        this.textboxTrailer3.addBeforeLookupModelSearchListener(event => event.filter.service_status = 'A');
        this.textboxTractor.addBeforeLookupModelSearchListener(event => event.filter.service_status = 'A');
        this.textboxDriverSeated.addBeforeLookupModelSearchListener(event => event.filter.is_active = 'Y');
        this.textboxDriverTwo.addBeforeLookupModelSearchListener(event => event.filter.is_active = 'Y');
    }

    async onEquipmentSelect(event: LookupModelSelectionEvent) {
        if (event.hasSelection() !== true && event.hasDeselection() !== true)
            return;
        const textbox = event.target as Textbox;
        const checkInProgressLocate = this.equipmentType === SeatingEquipmentType.TRACTOR;

        const vr = await this.validateEquipment(textbox.field, checkInProgressLocate);
        if (vr.errors.length > 0) {
            if (checkInProgressLocate) {
                this.handleTractorDriverValidationErrors(vr);
            }
            else {
                textbox.clear();
            }
        }
        else {
            this.clearTractorDriverValidationError();
        }

        if (getDispatchControlBoolean("driver_assignments", false) && !ObjectUtil.isEmptyObject(vr.enforcedDrivers)) {
            this.textboxDriverSeated.enabled = !vr.enforcedDrivers["driver_id"];
            this.textboxDriverTwo.enabled = !vr.enforcedDrivers["driver_two_id"];
        } else {
            if (!this.lockD1) this.textboxDriverSeated.enabled = true;
            if (!this.lockD2) this.textboxDriverTwo.enabled = true;
        }

        this.panelDrivers.displayData(this.activeRow, null, 0);
    }

    validateEquipment(field: string, checkInProgressLocate: boolean) {
        return EquipmentGroupValidation.validate({
            modelRow: this.activeRow, field,
            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            driver2Id: this.activeRow.get("driver_two_id", null),
            checkInProgressLocate
        });
    }

    private handleTractorDriverValidationErrors(vr: EquipmentGroupValidationResult) {
        this.panelError.visible = true;
        this.labelErrorText.text = ""; // Will be set by caller
        this.saveButton.enabled = false;

        // Check if any error's unique_id matches the tractor_id
        const tractorId = this.textboxTractor?.getDataValue();
        const hasTractorError = vr.errors.some(error => error.unique_id === tractorId);

        if (hasTractorError) {
            this.saveButton.disabledTooltip = `Tractor ${tractorId}'s current move must not be in progress.`;
        } else {
            this.saveButton.disabledTooltip = "Cannot locate tractor with drivers in progress on another movement.";
        }

        this.labelErrorText.text = vr.errors.reduce((res, error) => res += `ALERT - ${error.message}` + "\n", "");
        this.saveButton.removeFromHasChangedComponents();
    }

    private clearTractorDriverValidationError() {
        this.panelError.visible = false;
        // Re-add to data source change notifications and check enabled state
        if (this.saveButton.dataSource != null) {
            this.saveButton.dataSource.addHasChangedComponent(this.saveButton);
            this.saveButton.enabled = true;
        }
    }

    setupLocationComponents() {
        this.citystateSeatedLocation.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateSeatedLocation.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateSeatedLocation.textCombined.addChangeListener((event: ChangeEvent) => {
            if (event.newValue == "")
                this.locationSeatedLocation.clearAllFields();
        });
        this.locationSeatedLocation.textCombined.addChangeListener((event: ChangeEvent) => {
            if (event.newValue == "")
                this.citystateSeatedLocation.clearAllFields();
        });
    }

    /** This is an event handler for the onChange event of switchLocationType. */
    switchLocationTypeOnChange(event: ChangeEvent) {
        this.locationSeatedLocation.visible =
        this.locationSeatedLocation.required = !event.newValue;
        this.citystateSeatedLocation.visible = this.citystateSeatedLocation.required = event.newValue;

        if (!this.mainDataSource.isBoundComponent(this.locationSeatedLocation)) {
            this.mainDataSource.addBoundComponent(this.locationSeatedLocation);
        }

        if (!this.mainDataSource.isBoundComponent(this.citystateSeatedLocation)) {
            this.mainDataSource.addBoundComponent(this.citystateSeatedLocation);
        }

        if (event.newValue) {
            this.resetComponent(this.locationSeatedLocation);
            if (event.userInitiatedChange) {
                this.locationSeatedLocation.clearAllFields();
                this.mainDataSource.activeRow?.set("location_id", null);
                this.mainDataSource.activeRow?.set("address", null);
            }
        } else {
            this.resetComponent(this.citystateSeatedLocation);
            this.citystateSeatedLocation.clearAllFields();
        }
    }

    /** Need to remove the component from dataSource so it doesn't validate */
    resetComponent(comp: Component) {
        this.mainDataSource.removeBoundComponent(comp);
    }

    private async checkContinuity(modelRow: ModelRow): Promise<{ all: boolean; d1: any; d2: any, tractor: any; t1: any; t2: any; t3: any }> {
        const result: ModelSearchResult = await this.sourceSeatingContinuityCheck.search({
            driver_id: modelRow.get("driver_id"),
            driver_two_id: modelRow.get("driver_two_id"),
            tractor_id: modelRow.get("tractor_id"),
            trailer1_id: modelRow.get("trailer_id"),
            trailer2_id: modelRow.get("trailer2_id"),
            trailer3_id: modelRow.get("trailer3_id"),
            date: modelRow.get("date")
        });
        const response: ModelRow = result.getSingleModelRow();
        return { "all": response.getBoolean("continuity"), d1: response.getDateTime("continuity_error_d1", null),
            d2: response.getDateTime("continuity_error_d2", null), tractor: response.getDateTime("continuity_error_tractor", null),
            t1: response.getDateTime("continuity_error_trailer1", null), t2: response.getDateTime("continuity_error_trailer2", null),
            t3: response.getDateTime("continuity_error_trailer3", null)
        };
    }

    private createDriverContinuityPanel(modelRow: ModelRow, continuity: { all: boolean; d1: any; d2: any }): Panel {
        const driverOneName = modelRow.getFirstLookupModelData("driver_id")?.get("full_name") || "The selected Assigned Driver";
        const driverTwoName = modelRow.getFirstLookupModelData("driver_two_id")?.get("full_name") || "The selected Team Driver";
        const targetDriver = continuity.d1 ? driverOneName : driverTwoName;
        const availableTime = DateUtil.formatDateTime(continuity.d1 || continuity.d2);
        const targetTractor = modelRow.get("tractor_id") || "";

        const message = new Label({
            text: `${targetDriver} cannot be located before ${availableTime}.`
        });
        const question = new Label({
            fontBold: true, fontSize: "large",
            text: `Proceed with locating Tractor ${targetTractor} without a driver?`
        });
        return new Panel({width: 550, components: [message, question]});
    }

    private async sourceSeatingAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            if (this.equipmentType === SeatingEquipmentType.TRACTOR) {
                // Tractor
                this.activeRow.setValueAndLookupModelData("tractor_id", this.equipmentId, new ModelRow(
                    "lme/dispatch/asset/tractor",
                    false,
                    {id: this.equipmentId, name: this.equipmentId}
                ));
                this.textboxTrailer.required = false;
                const tractorRow = await new ModelTractor().searchSingle({ id: this.equipmentId }, "lme/dispatch/asset/Tractor");
                const driver1LmData = tractorRow?.getFirstLookupModelData("driver1_id") ?? null;
                const driver2LmData = tractorRow?.getFirstLookupModelData("driver2_id") ?? null;
                if (driver1LmData) {
                    this.driverId = driver1LmData.get("id");
                    this.activeRow.setValueAndLookupModelData("driver_id", this.driverId, driver1LmData);
                    if (getDispatchControlBoolean("driver_assignments", false)) {
                        this.textboxDriverSeated.enabled = false;
                        this.lockD1 = true;
                    }
                }
                if (driver2LmData) {
                    this.driver2Id = driver2LmData.get("id");
                    this.activeRow.setValueAndLookupModelData("driver_two_id", this.driver2Id, driver2LmData);
                    if (getDispatchControlBoolean("driver_assignments", false)) {
                        this.textboxDriverTwo.enabled = false;
                        this.lockD2 = true;
                    }
                }
                if ((tractorRow?.get("driver1_id") || tractorRow?.get("driver2_id")) && this.equipmentType === SeatingEquipmentType.TRACTOR) {
                    const fieldToSend = tractorRow?.get("driver1_id") ? "driver_id" : "driver_two_id";
                    const vr = await this.validateEquipment(fieldToSend, true);
                    if (vr.errors.length > 0) {
                        this.handleTractorDriverValidationErrors(vr);
                    }
                    else {
                        this.clearTractorDriverValidationError();
                    }
                }
            } else {
                // Trailer
                this.activeRow.setValueAndLookupModelData("trailer_id", this.equipmentId, new ModelRow(
                    "lme/dispatch/asset/trailer",
                    false,
                    {id: this.equipmentId, name: this.equipmentId}
                ));
                this.textboxTractor.required = false;
                this.textboxTractor.visible = false;
                this.panelDrivers.visible = false;
            }
        }
    }

    private sourceSeatingBeforeExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.ADD) {
            if (this.activeRow.isNull("driver_id") && !this.activeRow.isNull("driver_two_id")) {
                CommonDialogs.showError("Assigned Driver is required when a Team Driver is present.");
                event.stop();
                return;
            }
            if (this.activeRow.isNull("driver_id")) {
                this.activeRow.set("action", Action.UNSEAT);
            } else if (this.activeRow.get("driver_id") != this.driverId) {
                this.activeRow.set("action", Action.CHANGE_DRIVER);
            } else {
                this.activeRow.set("action", Action.SEAT);
            }
            const tractorChanged = this.activeRow.get("tractor_id") != this.equipmentId;
            this.activeRow.set("assigned_driver_changed", this.forceMasterfileUpdate || tractorChanged || this.activeRow.get("driver_id") != this.driverId);
            this.activeRow.set("team_driver_changed", this.forceMasterfileUpdate || tractorChanged || this.activeRow.get("driver_two_id") != this.driver2Id);
            this.activeRow.set("use_citystate", this.switchLocationType.checked);
        }
    }

    private sourceSeatingAfterExecution(event: DataSourceExecutionEvent) {
        const action = event.getAction();
        if (action == DataSourceAction.ADD) {
            this.slideOut().then(() => {
                this.doAfterSave?.();
                Toast.showSuccessToast(`${this.equipmentType} Successfully Updated`);
            });
        } else if (action == DataSourceAction.SEARCH) {
            if (this.activeRow.isNull("location_id")) {
                this.switchLocationType.checked = true;
            }
        }
    }

private createTrailerContinuityPanel(modelRow: ModelRow, continuity: { t1: any; t2: any; t3: any }): Panel {
    // Get trailer names or fallback to IDs
    const trailerOne = modelRow.get("trailer_id") || "Trailer 1";
    const trailerTwo =  modelRow.get("trailer2_id") || "Trailer 2";
    const trailerThree =  modelRow.get("trailer3_id") || "Trailer 3";
    const targetTractor = modelRow.get("tractor_id") || "";

    // Build message array for each trailer with continuity error
    const messages: string[] = [];
    if (continuity.t1) {
        messages.push(`${trailerOne} cannot be located before ${DateUtil.formatDateTime(continuity.t1)}.`);
    }
    if (continuity.t2) {
        messages.push(`${trailerTwo} cannot be located before ${DateUtil.formatDateTime(continuity.t2)}.`);
    }
    if (continuity.t3) {
        messages.push(`${trailerThree} cannot be located before ${DateUtil.formatDateTime(continuity.t3)}.`);
    }

    const message = new Label({
        text: messages.join("\n"),
        align: HorizontalAlignment.LEFT,
        width: 500
    });
    const question = new Label({
        fontBold: true,
        fontSize: "large",
        text: `Proceed with locating Tractor ${targetTractor} without these trailer(s)?`
    });
    return new Panel({ width: 550, components: [message, question] });
}

}
